import { Control, Controller } from "react-hook-form";
import TiptapEditor, { ITiptapEditorConfig } from "./TiptapEditor/TiptapEditor";
import { Button } from "@/components/shadcn/button";
import { NoxusFormLabel } from "./NoxusFormLabel";
import { useRef } from "react";
import { RichTextValue, getTemplateStringValue } from "./RichTextField";
import { Plus, Trash } from "lucide-react";
import { v4 as uuidv4 } from "uuid";
import { type AdvancedChipOption } from "./TiptapEditor/chipOptionsConfig";

interface ITipTapListField {
  options?: AdvancedChipOption[];
  control: Control<any>;
  defaultValue?: RichTextValue[];
  name: string;
  label?: string;
  tooltip?: string;
  placeholder?: string;
  unselectAllNodes?: () => any;
  isControlled?: boolean;
  config?: ITiptapEditorConfig;
  helperText?: string;
  number_of_lines?: number;
  required?: boolean;
  isDisabled?: boolean;
}

const EMPTY_ENTRY: RichTextValue = { text: "", definition: {}, isRaw: false };

const parseEntry = (entry: string | RichTextValue): RichTextValue => {
  if (!entry) return EMPTY_ENTRY;
  const unarrayed = Array.isArray(entry) ? entry[0] : entry;
  return unarrayed?.definition ? unarrayed : getTemplateStringValue(unarrayed);
};

interface ListEntry {
  id: string;
  value: RichTextValue;
}

const toListEntries = (values: RichTextValue[]): ListEntry[] =>
  values.map((v) => ({ id: uuidv4(), value: v }));

const TipTapListInner = (
  props: ITipTapListField & {
    entries: RichTextValue[];
    onChange: (values: RichTextValue[]) => void;
  }
) => {
  const listRef = useRef<ListEntry[]>(toListEntries(props.entries));

  // Keep ref in sync with external value length changes (e.g. form reset)
  if (listRef.current.length !== props.entries.length) {
    listRef.current = toListEntries(props.entries);
  }

  const handleEntryChange = (id: string, newValue: RichTextValue) => {
    listRef.current = listRef.current.map((e) =>
      e.id === id ? { ...e, value: newValue } : e
    );
    props.onChange(listRef.current.map((e) => e.value));
  };

  const handleAdd = () => {
    listRef.current = [
      ...listRef.current,
      { id: uuidv4(), value: { ...EMPTY_ENTRY } },
    ];
    props.onChange(listRef.current.map((e) => e.value));
  };

  const handleRemove = (id: string) => {
    if (listRef.current.length <= 1) return;
    listRef.current = listRef.current.filter((e) => e.id !== id);
    props.onChange(listRef.current.map((e) => e.value));
  };

  return (
    <div className="w-full">
      {props.label && (
        <NoxusFormLabel
          label={props.label}
          tooltip={props.tooltip}
          name={props.name}
          isRequired={props.required}
          isDisabled={props.isDisabled}
        />
      )}

      <div className="flex flex-col gap-2">
        {listRef.current.map((entry) => (
          <div key={entry.id} className="relative group">
            <TiptapEditor
              onChange={(val: RichTextValue) =>
                handleEntryChange(entry.id, val)
              }
              value={parseEntry(entry.value)}
              label={props.label}
              chipOptions={props.options}
              unselectAllNodes={props.unselectAllNodes}
              placeholder={props.placeholder}
              isControlled={props.isControlled}
              config={{
                hideChipOptions: true,
                hideToolbar: true,
                numberLines: props.number_of_lines,
              }}
              isDisabled={props.isDisabled}
            />
            {listRef.current.length > 1 && !props.isDisabled && (
              <Button
                type="button"
                variant="outline"
                size="iconsm"
                className="absolute -top-2 -right-2 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                onClick={() => handleRemove(entry.id)}
              >
                <Trash className="h-3 w-3" strokeWidth="1.33px" />
              </Button>
            )}
          </div>
        ))}
        <span className="text-xs font-regular leading-normal text-muted-foreground whitespace-pre-line">
          Insert{" "}
          <span className="font-semibold text-foreground bg-muted rounded-sm px-1.5">
            /
          </span>{" "}
          to see available inputs.
        </span>

        {!props.isDisabled && (
          <Button
            type="button"
            variant="outline"
            onClick={handleAdd}
            className="mt-1 mx-auto"
          >
            <Plus size="1.25rem" strokeWidth="1.33px" />
            Add entry
          </Button>
        )}
      </div>

      {props.helperText && (
        <span className="text-xs font-regular leading-normal text-muted-foreground mt-1.5 whitespace-pre-line">
          {props.helperText}
        </span>
      )}
    </div>
  );
};

export const TipTapListField = (props: ITipTapListField) => {
  return (
    <Controller
      control={props.control}
      name={props.name}
      defaultValue={props.defaultValue ?? [EMPTY_ENTRY]}
      rules={{
        validate: props.required
          ? (value: RichTextValue[]) =>
              Array.isArray(value) &&
              value.length > 0 &&
              value.every((v) => v && v.text.length > 0)
          : undefined,
      }}
      render={({ field: { onChange, value } }) => {
        const entries: RichTextValue[] =
          Array.isArray(value) && value.length > 0 ? value : [EMPTY_ENTRY];

        return (
          <TipTapListInner {...props} entries={entries} onChange={onChange} />
        );
      }}
    />
  );
};
